// Friendly Russian copy for the technical errors fetch / our api layer
// occasionally surface to users. Use at the *UI boundary* — Alert title /
// message, inline form error, toast — so that Sentry still sees the raw
// technical reason but the user reads something they can act on.
//
// Don't pre-translate inside api.ts call sites: the technical message
// stays useful for analytics, logs, and dev tooling; we only swap it
// when it's about to be shown to a human.
//
// Coverage (in order of detection)
// ================================
// 1. DOMException — `TimeoutError` from our own `fetchWithTimeout` ceiling
//    in `api.ts`, `AbortError` from caller-side AbortController.
// 2. TypeError — `Failed to fetch` (DNS / TCP / TLS / hard transport
//    failure). The RU edge fallback already retries these once.
// 3. Native Error with browser-specific abort phrasing
//    («Fetch is aborted», «The user aborted a request»).
// 4. ApiError thrown by `handleResponse` — `{ message, status }`.
//    Prefer the BE message when it looks human-readable (Cyrillic
//    heuristic — backend already returns localized reasons for known
//    domain errors); otherwise use a status-keyed default.

/** Strings the browser puts on a plain Error when a fetch is cancelled. */
const ABORT_NATIVE_MESSAGES = new Set([
  'Fetch is aborted',
  'The user aborted a request.',
  'The user aborted a request',
  'signal is aborted without reason',
  'The operation was aborted.',
]);

/**
 * Thrown by the chunked uploader (`chunkedUpload` in api.ts) when a chunk
 * PUT fails. CORE-205 — carries the HTTP `status` as a structured field (not
 * just buried in the message) so the UI boundary can map it to friendly copy
 * (e.g. 413 → «Контент слишком большой…») instead of leaking the raw, doubled
 * «Chunk 0 failed after 3 retries: Chunk 0 failed: 413». The message stays
 * technical for logs/Sentry; only `friendlyApiError` is shown to the user.
 */
export class ChunkUploadError extends Error {
  readonly status?: number;
  constructor(message: string, status?: number, options?: { cause?: unknown }) {
    super(message, options as ErrorOptions);
    this.name = 'ChunkUploadError';
    this.status = status;
  }
}

/**
 * Thrown by the presigned direct-to-S3 uploader (`presignAndPutVideo` /
 * `presignAndPutPhoto` in api.ts). CORE-232 — the PUT goes straight to
 * S3/Yandex, so a failure surfaces as a bare «S3 upload network error» /
 * «S3 upload failed: 502». That raw English string was leaking into the UI
 * (Tima, CORE-232). Carry the optional HTTP `status` so `friendlyApiError`
 * can map it (413 → «Контент слишком большой…»); a network drop has no status
 * and gets the generic upload copy. The technical message stays for Sentry.
 */
export class UploadError extends Error {
  readonly status?: number;
  constructor(message: string, status?: number, options?: { cause?: unknown }) {
    super(message, options as ErrorOptions);
    this.name = 'UploadError';
    this.status = status;
  }
}

/**
 * CORE-722 — the photo is not something we can publish AT ALL (a HEIC that
 * this browser cannot decode, or any format outside JPEG/PNG/WebP). A verdict,
 * not a transport failure, and it carries copy that already tells the author
 * what to do about it.
 *
 * It exists as its own type because both layers above treated it as a plain
 * upload hiccup and destroyed it: `createPost` caught it as «presign failed»
 * and re-sent the same unusable file down the legacy multipart path (which the
 * API then refused with a bare 400), and `friendlyApiError` rewrote any
 * status-less `UploadError` into the generic «Не удалось загрузить файл». The
 * author was left with «проверьте введённые данные» about a form that was
 * filled in correctly. Retrying is pointless — only a different file helps —
 * so it must never trip a fallback or a retry.
 */
export class PhotoFormatError extends UploadError {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, undefined, options);
    this.name = 'PhotoFormatError';
  }
}

/**
 * Thrown by the auth-required upload helpers (`presignAndPutVideo` /
 * `presignAndPutCover` / `chunkedUpload` in api.ts) when there is no usable
 * JWT — either the token was never present or a mid-session 401 already
 * cleared it (`handleResponse` → `storage.removeToken()`), leaving the UI in
 * a "ghost authenticated" state (user object still set, token gone).
 *
 * CORE-415 — those helpers used to `throw new Error('Not authenticated')`, a
 * status-less plain Error whose English message leaked straight into the
 * practice-upload error box (Veronika, prod REACT-NATIVE-19): `friendlyApiError`
 * fell through to its last-resort branch and echoed the raw string, and the
 * «Повторить» button just re-threw (a missing token can't be recovered by
 * retry). Carrying `status: 401` routes it to the existing «Сессия истекла»
 * copy AND lets the UI boundary reconcile the ghost state (re-login) instead
 * of offering a dead-end retry.
 */
export class AuthRequiredError extends Error {
  readonly status = 401 as const;
  constructor(message = 'Not authenticated', options?: { cause?: unknown }) {
    super(message, options as ErrorOptions);
    this.name = 'AuthRequiredError';
  }
}

/** True when an error means "no valid session" — the caller should send the
 *  user to re-login rather than retry. Matches our typed AuthRequiredError, a
 *  bare 401, or the legacy plain `Error('Not authenticated')`. */
export function isAuthError(err: unknown): boolean {
  const e = err as { status?: number; message?: string } | null;
  if (!e) return false;
  return e instanceof AuthRequiredError
    || e.status === 401
    || e.message === 'Not authenticated';
}

/**
 * True when an API error is a content-moderation rejection rather than a
 * generic failure. The .NET `ContentModerationGate` returns HTTP 422 with a
 * message containing "flagged by automated moderation"
 * (`ContentModerationGate.BlockReason`). Use at the UI boundary to show the
 * branded ModerationWarning banner instead of the generic "проверьте данные"
 * copy — the rejection reason (policy violation) is then unmistakable.
 */
export function isModerationBlock(err: unknown): boolean {
  const e = err as {
    status?: number; message?: string; Message?: string; code?: string; Code?: string;
  } | null;
  if (!e || e.status !== 422) return false;
  // CORE-196 — код надёжнее подстроки, но подстрока остаётся: старые сборки
  // бэкенда кода ещё не присылают, и форма не должна из-за этого показывать
  // человеку сырое английское сообщение.
  const code = e.code ?? e.Code;
  if (code) return code.startsWith('moderation_');
  const msg = (e.message ?? e.Message ?? '').toLowerCase();
  return msg.includes('moderation') || msg.includes('flagged');
}

/**
 * Текст отказа по нарушению — для форм с обычной строкой ошибки, без
 * брендового баннера `ModerationWarning`. Подменяет английский `BlockReason`
 * бэкенда, чтобы тот не утекал в интерфейс.
 *
 * Формулировка без «содержимое нарушает правила сообщества»: так пишет
 * протокол, а не человек. Говорим, что случилось («не прошло проверку»), что
 * делать («поправьте и отправьте снова») и снимаем главный страх — что текст
 * пропал. Безличное «это» подходит и тексту, и картинке.
 */
export const MODERATION_BLOCK_MESSAGE =
  'Это не прошло проверку — так у нас публиковать нельзя. Поправьте и отправьте снова, написанное сохранено.';

/**
 * CORE-196 — отказ из-за того, что проверка не сработала, а не из-за нарушения.
 *
 * Бэкенд перестал публиковать контент при недоступности провайдера модерации:
 * пост живёт в ленте долго и виден всем, поэтому «пропустить на всякий случай»
 * дороже, чем попросить повторить. Но человек при этом ничего не нарушал, и
 * показывать ему «содержимое нарушает правила» — значит обвинить его за нашу
 * недоступность.
 *
 * Различаем по коду в теле ответа (`Code`), а не по подстроке в человеческом
 * сообщении: сообщение переводится и переписывается, а код нет.
 */
export function isModerationUnavailable(err: unknown): boolean {
  const e = err as { status?: number; code?: string; Code?: string } | null;
  if (!e || e.status !== 422) return false;
  return (e.code ?? e.Code) === 'moderation_unavailable';
}

/**
 * Текст для случая, когда проверить не удалось. Просьба повторить, а не
 * обвинение: причина на нашей стороне, человек ничего не нарушал.
 *
 * «Написанное сохранено» — не украшение, а обещание, которое держат все формы:
 * при отказе поле не очищается (см. useModerationNotice и разбор форм в
 * docs/results). Если где-то это перестанет быть правдой, менять надо форму,
 * а не текст.
 */
export const MODERATION_UNAVAILABLE_MESSAGE =
  'Не удалось проверить — попробуйте ещё раз. Написанное сохранено, набирать заново не нужно.';

/**
 * Единый текст отказа модерации для любой формы: сначала различаем причину,
 * потом показываем. Вызывающему не нужно знать про коды — он спрашивает
 * «что сказать человеку».
 */
export function moderationRefusalMessage(err: unknown): string {
  return isModerationUnavailable(err)
    ? MODERATION_UNAVAILABLE_MESSAGE
    : MODERATION_BLOCK_MESSAGE;
}

/** True when a raw BE message string (not the error object) is a moderation
 *  block — for translators that only receive the message text. */
export function isModerationBlockMessage(raw: string | undefined | null): boolean {
  const msg = (raw ?? '').toLowerCase();
  return msg.includes('flagged by automated moderation') ||
    (msg.includes('moderation') && msg.includes('flagged'));
}

/**
 * CORE-819 / CORE-769 §5.4 — the shape of the `capability_required` 403 body the
 * backend returns when a Live host/co-host action is refused by the frozen
 * session capability snapshot (WatchPartyController.cs `CapabilityDenied`). The
 * FE only READS these fields; it never re-derives a right from them.
 *
 * `currentLevelCode` is the SESSION's frozen level (CORE-769 §6), NOT the
 * viewer's — the honest answer is that the decision was made against the level
 * the session was created at. `requiresMasterRole: true` means no level opens
 * the capability, so the copy must not dangle progress toward one.
 */
export interface CapabilityDeniedBody {
  status?: number;
  code?: string;
  capability?: string;
  requiredLevelCode?: string | null;
  requiredLevelName?: string | null;
  currentLevelCode?: string | null;
  requiresMasterRole?: boolean;
}

/**
 * True when an API error is the `capability_required` 403 (CORE-819). A gated
 * action was refused by the session's frozen capability snapshot — a definitive
 * "this session can't do that", not a transient failure to retry.
 */
export function isCapabilityDenied(err: unknown): err is CapabilityDeniedBody {
  const e = err as CapabilityDeniedBody | null;
  return !!e && e.status === 403 && e.code === 'capability_required';
}

/**
 * Honest, brand-voice RU copy for a `capability_required` 403 (CORE-769 §9).
 * Keyed only on the refusal body's own fields — no client-side capability
 * matrix (запрет: «дублирующей матрицы прав на фронте быть не должно»). Never
 * advises a page refresh (a frozen snapshot won't change) and never presents
 * the session's frozen level as the viewer's own.
 */
export function capabilityDeniedMessage(err: unknown): string {
  const body = (err ?? {}) as CapabilityDeniedBody;
  // Reserved for the confirmed master role: no amount of levelling opens it, so
  // don't mention a level at all.
  if (body.requiresMasterRole) {
    return 'Эта возможность доступна только мастерам.';
  }
  // A level would grant it — name the level, but stay honest that THIS session
  // (frozen below it) can't use the capability.
  if (body.requiredLevelName) {
    return `Эта возможность открывается с уровня «${body.requiredLevelName}». В этой сессии она недоступна.`;
  }
  return 'Эта возможность недоступна в этой сессии.';
}

/**
 * CORE-819 — honest RU copy for a REFUSED gated Live action (studio / active
 * session), whether or not the server attached a capability body. Use this at
 * the gated Live action call sites (recording toggle, practice control,
 * co-host / moderation) instead of the bare `friendlyApiError`.
 *
 *  - `capability_required` 403 → the structured 3-branch mapper above.
 *  - a *bodyless* 403 → an honest generic session hint. The backend's
 *    `recording/start`, `recording/stop`, `recording/resume` and the host-only
 *    co-host endpoints answer a non-host (incl. a delegated co-host) with a bare
 *    `Forbid()` — 403, empty body, no `code: capability_required`. That is a
 *    known BE contract gap (those paths never route through the capability
 *    layer; see the CORE-819 comment / backend follow-up). The FE must still
 *    read as human, and must NOT show the generic-403 «обновите страницу» copy:
 *    a frozen session snapshot (CORE-769 §6) won't change on refresh, so that
 *    advice is a dead end.
 *  - anything else (503 not-configured, 409 conflict, network) → the shared
 *    `friendlyApiError`, unchanged.
 *
 * The button never dies silently and never shows raw English backend text.
 */
export function capabilityRefusalMessage(err: unknown): string {
  if (isCapabilityDenied(err)) return capabilityDeniedMessage(err);
  const status = (err as { status?: number } | null)?.status;
  if (status === 403) return 'Это действие недоступно в текущей сессии.';
  return friendlyApiError(err);
}

// Russian labels for the fields the API validates on the content/profile forms.
const VALIDATION_FIELD_LABELS: Record<string, string> = {
  Title: 'Название',
  Description: 'Описание',
  Category: 'Категория',
  Categories: 'Направления',
  Language: 'Язык',
  Visibility: 'Видимость',
  Subtitle: 'Подзаголовок',
  Location: 'Место',
};

/**
 * Turn an ASP.NET ValidationProblemDetails `errors` map into a single Russian
 * sentence. Special-cases the common `[MaxLength]` violation into a precise
 * «Поле «X» слишком длинное — максимум N символов.»; otherwise falls back to the
 * first error's text (if Russian) or a generic prompt. Returns null when there's
 * nothing usable so the caller keeps its own copy.
 */
function validationProblemMessage(errors: Record<string, string[]> | undefined): string | null {
  if (!errors || typeof errors !== 'object') return null;
  const firstField = Object.keys(errors)[0];
  if (!firstField) return null;
  const raw = errors[firstField]?.[0] ?? '';
  const label = VALIDATION_FIELD_LABELS[firstField] ?? firstField;

  const maxLen = raw.match(/maximum length of '(\d+)'/);
  if (maxLen) return `Поле «${label}» слишком длинное — максимум ${maxLen[1]} символов.`;

  if (/required/i.test(raw)) return `Заполните поле «${label}».`;
  if (/[а-яА-Я]/.test(raw)) return raw; // already Russian from the BE
  return 'Проверьте правильность заполнения полей.';
}

/** Russian plural picker: pluralRu(2, 'день', 'дня', 'дней') → 'дня'. */
function pluralRu(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
  return many;
}

/**
 * Human wait in RU, split into days / hours / minutes so a long lead-time reads
 * «через 2 дня 3 ч» instead of «через 3033 мин» (tester feedback on the
 * pre-start error). Minutes are dropped once the wait is a day or more — at that
 * scale they're noise. Caller guards totalMin > 0.
 */
function formatCountdownRu(totalMin: number): string {
  const days = Math.floor(totalMin / 1440);
  const hours = Math.floor((totalMin % 1440) / 60);
  const mins = totalMin % 60;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days} ${pluralRu(days, 'день', 'дня', 'дней')}`);
  if (hours > 0) parts.push(`${hours} ч`);
  if (mins > 0 && days === 0) parts.push(`${mins} мин`);
  return parts.length ? parts.join(' ') : '1 мин';
}

/**
 * Translate exact English backend messages that are already user-facing in
 * intent, but must not be shown raw in the Russian UI. Keep this whitelist
 * narrow so arbitrary technical English still falls through to status-based
 * friendly copy.
 */
function knownBackendMessage(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const text = raw.trim();
  const tooLarge = text.match(/^File too large\. Maximum size: ([0-9]+(?:[.,][0-9]+)?)\s*([KMGT]?B)\.$/i);
  if (tooLarge) {
    const [, value, unitRaw] = tooLarge;
    const unit = unitRaw.toUpperCase();
    const unitLabel: Record<string, string> = {
      B: 'Б',
      KB: 'КБ',
      MB: 'МБ',
      GB: 'ГБ',
      TB: 'ТБ',
    };
    return `Файл слишком большой. Максимальный размер: ${value.replace('.', ',')} ${unitLabel[unit] ?? unit}.`;
  }

  // CORE-722 — the feed API's verdict on a photo it cannot store
  // (`CommunityFeedController.ValidateImage`). Untranslated it collapses into
  // the generic 400 copy «проверьте введённые данные», which sends the author
  // back to a form that has nothing wrong with it. The client refuses these
  // formats before uploading now, so this covers what still gets through —
  // native pickers and anything published outside the composer.
  if (text === 'Invalid file type. Allowed: JPEG, PNG, WebP.') {
    return 'Такой формат фото мы показать не сможем. Подойдут JPEG, PNG или WebP.';
  }

  // CORE-1257 — `WatchPartyService.DeleteRecordingAsync` refuses (409) while
  // the recording is still running.
  if (text === 'Stop the recording before deleting it.') {
    return 'Запись ещё идёт. Остановите её, и тогда её можно будет удалить.';
  }

  // CORE-521 — the three answers the feed API gives about a post's video
  // (`CommunityFeedController.ValidateVideoUrlAsync`). Untranslated they all
  // collapse into the generic 400 copy «проверьте введённые данные», which
  // tells the author to check a form that has nothing wrong with it — the QA
  // report for the web video post reads «понятного сообщения об ошибке нет».
  if (text === 'Invalid video URL.') {
    return 'Не получилось прикрепить это видео. Загрузите его ещё раз.';
  }
  if (text === 'Uploaded video not found. Please retry.') {
    return 'Видео не доехало до хранилища. Попробуйте загрузить его ещё раз.';
  }
  const videoTooLarge = text.match(/^A video may be at most ([0-9]+)MB\.$/);
  if (videoTooLarge) {
    return `Видео до ${videoTooLarge[1]} МБ, а это больше. Выберите файл поменьше.`;
  }

  // CORE-586 pre-start window — the master tried to start a scheduled live too
  // early. Raw BE (leaked to the UI as HTTP 400, CORE-669): "This live is
  // scheduled for YYYY-MM-DD HH:MM UTC. You can start it no earlier than N
  // minutes before the scheduled time." Parse the UTC start from the message
  // and tell the master WHEN they can begin, in their own local time. Time is
  // formatted by hand (no Intl — Hermes' Intl support is partial on native).
  const tooEarly = text.match(
    /scheduled for (\d{4}-\d{2}-\d{2}) (\d{2}:\d{2})\s*UTC[\s\S]*?no earlier than (\d+)\s*minutes/i,
  );
  if (tooEarly) {
    const [, ymd, hm, minsRaw] = tooEarly;
    const mins = parseInt(minsRaw, 10) || 5;
    const scheduled = new Date(`${ymd}T${hm}:00Z`);
    if (!Number.isNaN(scheduled.getTime())) {
      const openAt = new Date(scheduled.getTime() - mins * 60_000);
      const hhmm = `${String(openAt.getHours()).padStart(2, '0')}:${String(openAt.getMinutes()).padStart(2, '0')}`;
      const leftMin = Math.ceil((openAt.getTime() - Date.now()) / 60_000);
      const countdown = leftMin > 0 ? ` (через ${formatCountdownRu(leftMin)})` : '';
      return `Ещё рано начинать Live-сессию — открыть можно за ${mins} мин до старта, в ${hhmm}${countdown}.`;
    }
    return `Ещё рано начинать Live-сессию — открыть можно за ${mins} мин до старта.`;
  }

  return null;
}

export function friendlyApiError(err: unknown): string {
  // 0-. CORE-196 — отказ модерации раньше доходил сюда и превращался в
  //     «Проверьте введённые данные» (общий текст для 422): человек видел
  //     упрёк к форме, которую заполнил правильно, и не понимал, что делать.
  //     Теперь любая форма, уже вызывающая friendlyApiError, показывает
  //     верный текст — и разный для «нарушает правила» и «не смогли
  //     проверить» — без единой правки на своей стороне.
  if (isModerationBlock(err)) return moderationRefusalMessage(err);

  // 0. Chunked-upload failures wrap the underlying cause. When there's a
  //    structured HTTP status, fall through to the status-keyed copy below;
  //    when there isn't (network/transport drop mid-upload), classify by the
  //    cause so we never surface the raw «Chunk N failed after 3 retries: …».
  if (err instanceof ChunkUploadError && err.status == null && err.cause != null) {
    return friendlyApiError(err.cause);
  }

  // 0a. CORE-722 — a format verdict already carries the copy the author needs
  //     («сохрани как JPEG…»). It must be checked BEFORE the UploadError branch
  //     below, which would otherwise overwrite it with the generic upload copy
  //     and drop the one piece of information that makes the error actionable.
  if (err instanceof PhotoFormatError) return err.message;

  // 0b. Presigned-upload failures (CORE-232). A network drop mid-PUT has no
  //     status → generic upload copy; an HTTP status (e.g. 413) falls through
  //     to the status-keyed switch below. Never leak «S3 upload network error».
  if (err instanceof UploadError && err.status == null) {
    return 'Не удалось загрузить файл. Проверьте соединение и попробуйте ещё раз.';
  }

  // 0c. Missing/expired session (CORE-415). AuthRequiredError carries
  //     status:401 and would map via the switch below anyway, but a legacy
  //     status-less `Error('Not authenticated')` would otherwise slip through
  //     to the last-resort branch and leak the raw English string into the UI
  //     (prod REACT-NATIVE-19). Map both to the same «Сессия истекла» copy the
  //     401 case uses, so the user gets a real "log in again" cue.
  if (
    err instanceof AuthRequiredError ||
    (err instanceof Error && err.message === 'Not authenticated')
  ) {
    return 'Сессия истекла. Войдите снова, пожалуйста.';
  }

  // 1. DOMException — distinguish TimeoutError (our fetchWithTimeout fired)
  //    from AbortError (caller cancelled, e.g. component unmount).
  if (typeof DOMException !== 'undefined' && err instanceof DOMException) {
    if (err.name === 'TimeoutError') {
      return 'Запрос идёт слишком долго. Проверьте соединение и попробуйте ещё раз.';
    }
    if (err.name === 'AbortError') {
      return 'Запрос прерван. Попробуйте ещё раз.';
    }
  }

  // 2. TypeError surfaces as `Failed to fetch` for DNS / TLS / CORS /
  //    «no response received» on web. RN puts other names here too;
  //    treat them all as network trouble.
  if (err instanceof TypeError) {
    return 'Нет соединения с сервером. Проверьте интернет и попробуйте ещё раз.';
  }

  // 3. Native abort phrasing on a plain Error (some envs don't wrap
  //    the abort as a DOMException — happens on RN web in particular).
  if (err instanceof Error && ABORT_NATIVE_MESSAGES.has(err.message)) {
    return 'Запрос прерван. Попробуйте ещё раз.';
  }

  // 4. ApiError from handleResponse — `{ message?, Message?, status }`.
  const anyErr = err as { status?: number; message?: string; Message?: string };
  const status = anyErr?.status;

  // CORE-819 — a `capability_required` 403 is an honest "this session can't do
  // that", not a transient error. Map it to a soft hint BEFORE the generic
  // status switch below (whose 403 branch tells the user to refresh — wrong
  // advice under a frozen session snapshot, CORE-769 §6) and before the BE
  // message passthrough (the BE sentence is English and not user-facing copy).
  if (isCapabilityDenied(err)) return capabilityDeniedMessage(err);

  const beMessage = anyErr?.message ?? anyErr?.Message;
  const translatedBackendMessage = knownBackendMessage(beMessage);
  if (translatedBackendMessage) return translatedBackendMessage;

  if (typeof status === 'number') {
    // Prefer the BE-provided message when it's already in Russian and
    // not our own fallback template — backends often pin down the
    // exact domain reason («Контент слишком большой», «Этот email уже
    // зарегистрирован») and our generic copy would lose detail.
    if (
      beMessage &&
      typeof beMessage === 'string' &&
      /[а-яА-Я]/.test(beMessage) &&
      !beMessage.startsWith('Request failed with status')
    ) {
      return beMessage;
    }

    // ASP.NET ValidationProblemDetails ([ApiController]) — surface the first
    // field error as precise Russian copy instead of the generic 400 fallback.
    // Without this, a too-long description just read «проверьте введённые данные»
    // with no hint which field was wrong.
    const vp = validationProblemMessage((anyErr as { errors?: Record<string, string[]> }).errors);
    if (vp) return vp;

    switch (status) {
      case 400:
      case 422:
        return 'Не удалось сохранить — проверьте введённые данные.';
      case 401:
        return 'Сессия истекла. Войдите снова, пожалуйста.';
      case 403:
        return 'Действие сейчас недоступно. Если уверены, что у вас есть доступ — обновите страницу и попробуйте ещё раз.';
      case 404:
        return 'Не нашли то, что искали.';
      case 408:
        return 'Сервер слишком долго отвечал. Попробуйте ещё раз.';
      case 409:
        return 'Что-то изменилось параллельно — обновите страницу и попробуйте ещё раз.';
      case 413:
        return 'Контент слишком большой. Сократите текст, уменьшите размер вложений и попробуйте ещё раз.';
      case 415:
        return 'Этот тип файла пока не поддерживается.';
      case 429:
        return 'Слишком много запросов. Подождите минуту и попробуйте ещё раз.';
      default:
        if (status >= 500) {
          return 'Сервер сейчас недоступен. Попробуйте через минуту.';
        }
    }
  }

  // 5. Last resort — fall back to BE message if it's at least somewhat
  //    human-readable, else a generic copy. The «Request failed with
  //    status N» template is our own and never useful to show — and the
  //    uploader's «Chunk N failed…» message (CORE-205) must never reach a
  //    human either, so ChunkUploadError always gets the generic copy.
  if (
    !(err instanceof ChunkUploadError) &&
    beMessage &&
    typeof beMessage === 'string' &&
    !beMessage.startsWith('Request failed with status')
  ) {
    return beMessage;
  }
  return 'Что-то пошло не так. Попробуйте ещё раз.';
}

/**
 * Statuses that mean «the server refused this request, and the user is already
 * reading why» — a validation message, a moderation block, a wrong password, a
 * link that no longer resolves. Deliberately excludes 408 and 429: a timeout
 * and a rate-limit are infrastructure signals, not a refusal aimed at the
 * person, and we do want to hear about them.
 */
const EXPECTED_REFUSAL_STATUSES = new Set([400, 401, 403, 404, 409, 410, 413, 415, 422]);

/**
 * CORE-1099 — is this failure the normal, user-visible end of a user action?
 *
 * Every `captureError` в этом репозитории схлопывается в Sentry в группы по
 * culprit `captureError`, поэтому ожидаемый 4xx не просто занимает место: он
 * ложится в одну группу с настоящими сбоями и прячет их. Так дважды за два дня
 * терялись реальные отказы — CORE-1058 (404 завершённой комнаты) и CORE-1092
 * (тот же 404 на втором экране, где рядом лежали два 500).
 *
 * Границу проводим по статусу, а не по тексту: текст пишет бэкенд и меняет его
 * без нас. Всё, что не 4xx-отказ — сеть без статуса, разбор ответа, 5xx, 408,
 * 429 — по-прежнему репортится.
 */
export function isExpectedUserRefusal(err: unknown): boolean {
  const status = (err as { status?: unknown } | null | undefined)?.status;
  return typeof status === 'number' && EXPECTED_REFUSAL_STATUSES.has(status);
}

/**
 * Инверсия `isExpectedUserRefusal` для чтения на call-site:
 * `if (shouldReportApiError(e)) captureError(e, …)`.
 *
 * Не путать с `shouldReportRoomLoadError` из `src/utils/roomAvailability.ts`:
 * то правило намеренно уже — при загрузке комнаты ожидаемым считается только
 * 404/410, а 403 обязан репортиться (CORE-1058 AC3).
 */
export function shouldReportApiError(err: unknown): boolean {
  return !isExpectedUserRefusal(err);
}
